"use client";

import {
  Body,
  Button,
  buttonVariants,
  Flag,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  XMarkIcon,
} from "@scott/ui-d2c";
import type { CountryNavItem } from "@scott/ui-d2c/types";
import { usePathname } from "next/navigation";
import { useState } from "react";
import {
  COUNTRY_NAVIGATION_BAR_COOKIE_NAME,
  COUNTRY_NAVIGATION_BAR_SELECT_LOCATION_URL,
} from "@/utils/constants";
import {
  getUrlWithSelectedCountryAndLanguage,
  setCookie,
} from "@/utils/header.utils";

export interface countryNavigationProps {
  componentData: CountryNavItem;
  className?: string;
}

const CountryNavigationBar = ({ componentData }: countryNavigationProps) => {
  const {
    countryCode,
    selectLocationLabel,
    buttonLabel,
    mainText,
    countryLanguageOptions,
  } = componentData;

  const defaultOption =
    countryLanguageOptions.find((option) => option.isSelected) ??
    countryLanguageOptions[0];
  const pathname = usePathname();

  const [selectedLocationUrl, setSelectedLocationUrl] = useState(
    defaultOption.url
  );
  const [isClose, setIsClose] = useState(false);

  const url = getUrlWithSelectedCountryAndLanguage(
    pathname,
    selectedLocationUrl
  );

  const handleLocationChange = (value: string) => {
    setSelectedLocationUrl(value);
  };

  const handleCountryNavigationBarClose = () => {
    const COOKIE_EXPIRY_DAYS = 30;
    setIsClose(true);
    setCookie(COUNTRY_NAVIGATION_BAR_COOKIE_NAME, "true", COOKIE_EXPIRY_DAYS);
  };

  return (
    <>
      {!isClose && (
        <div className="!py-3 container px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-[auto,1fr] items-center gap-4 md:flex-row lg:flex lg:gap-3">
            <div className="order-1 lg:order-1 lg:mr-5 lg:basis-1/2 xl:basis-2/3">
              <Body className="py-0 text-left" size="sm">
                {mainText}
              </Body>
            </div>
            <div className="order-3 col-span-2 flex flex-row gap-3 lg:order-2 lg:basis-1/2 xl:basis-1/3">
              <div className="w-full">
                <Select
                  onValueChange={handleLocationChange}
                  value={selectedLocationUrl}
                >
                  <SelectTrigger className="!text-sm h-10">
                    <SelectValue placeholder={selectLocationLabel} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup className="text-neutral-dark">
                      {countryLanguageOptions.map(({ label, url }) => (
                        <SelectItem key={label} value={url}>
                          <div className="flex flex-row items-center">
                            <Flag countryCode={countryCode} />
                            {label}
                          </div>
                        </SelectItem>
                      ))}
                      <SelectItem
                        className="text-neutral-dark"
                        value={COUNTRY_NAVIGATION_BAR_SELECT_LOCATION_URL}
                      >
                        {selectLocationLabel}
                      </SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              <a
                className={buttonVariants({ variant: "outline", size: "md" })}
                href={url}
              >
                {buttonLabel}
              </a>
            </div>
            <div className="order-2 self-start lg:order-3 lg:self-center">
              <Button
                icon={<XMarkIcon className="text-neutral-dark" size="1x" />}
                onClick={handleCountryNavigationBarClose}
                size={"icon"}
                variant={"ghost"}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

CountryNavigationBar.displayName = "CountryNavigationBar";

export { CountryNavigationBar };
